'use client';

import { Modal, type ModalHandle } from '@lfg/ui/components';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import Image from 'next/image';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';

import { icons } from '@/assets/icons';

export interface GoogleCalendarModalProps {
  name: string;
  email: string;
  companyName: string;
  country: string;
  numberOfEmployees: string;
}

export interface GoogleCalendarModalHandle {
  show: (data: GoogleCalendarModalProps) => void;
  hide: () => void;
}

export interface GoogleCalendarModalComponentProps {
  onEventScheduled?: () => void;
}

// const GOOGLE_CALENDAR_URL =
//   'https://calendar.google.com/calendar/appointments/schedules/AcZssZ3puExrH5aFaffRAxizOZWzVJXv-vZTEifdxduRQhVd909mLs96xbBdziUEcrm34X30lifXjJvc';

const GOOGLE_CALENDAR_URL =
  'https://calendar.google.com/calendar/appointments/schedules/AcZssZ3Q91vIs4TgL4PC4y0S5NYS8ORWQrnDMR4Mf2i2FO4seRAIcN28kwxVRJZSxrFTWqR5WcUckUe5?gv=true';

const GoogleCalendarModal = forwardRef<GoogleCalendarModalHandle, GoogleCalendarModalComponentProps>(
  ({ onEventScheduled }, ref) => {
    const modalRef = useRef<ModalHandle>(null);

    const onShow = useCallback(() => {
      modalRef.current?.show();
    }, []);

    const onHide = useCallback(() => {
      modalRef.current?.hide();
    }, []);

    useImperativeHandle(ref, () => ({ show: onShow, hide: onHide }), [onShow, onHide]);

    return (
      <Modal ref={modalRef} bgColor={'#060912'} onClose={onHide} width='1120px'>
        <Stack
          gap={3}
          flexGrow={1}
          height='80vh'
          alignItems={'center'}
          position={'relative'}
          padding={{ xs: 0, sm: 3 }}>
          <Stack position={'absolute'} top={0} right={0} zIndex={1000}>
            <IconButton onClick={onHide} sx={{ padding: 0 }}>
              <Image width={24} height={24} src={icons.ic_x || '/icons/close.svg'} alt='close' />
            </IconButton>
          </Stack>

          <Stack gap={1} alignItems={'center'} textAlign={'center'} width='100%'>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: '20px', sm: '24px' },
                lineHeight: '32px',
                color: 'white',
              }}>
              Thank you for your interest!
            </Typography>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: { xs: '16px', sm: '18px' },
                lineHeight: '24px',
                color: 'rgba(255, 255, 255, 0.75)',
              }}>
              Schedule a meeting with our team at your convenience.
            </Typography>
          </Stack>

          <Box
            sx={{
              width: '100%',
              height: '100%',
              bgcolor: 'white',
              borderRadius: '8px',
              overflow: 'hidden',
            }}>
            <iframe src={GOOGLE_CALENDAR_URL} style={{ border: 0 }} width='100%' height='100%' frameBorder='0'></iframe>
          </Box>
        </Stack>
      </Modal>
    );
  },
);

GoogleCalendarModal.displayName = 'GoogleCalendarModal';

export default GoogleCalendarModal;
